import { useEffect, useState } from "react";

import displayToast from "@common/components/Toast/displayToast";
import useScoringConfig, { ScoringConfigKeys } from "@main/pages/Release/consumers/useScoringConfig";
import { ThresholdRowModel } from "@main/pages/Release/views/MetricDetails/shared/MetricRow";

export default function useNetworkSpeedScoring(refetchReleaseStatus: () => void) {
  const { scoringConfig, scoringConfigLoading, scoringConfigError, saveScoringConfig, isSaving } = useScoringConfig(
    ScoringConfigKeys.network_speed,
  );

  const [thresholds, setThresholds] = useState<number[]>(() => THRESHOLDS.map(() => 0));
  const [weightA, setWeightA] = useState(0);
  const [weightB, setWeightB] = useState(0);

  useEffect(() => {
    const config = scoringConfig?.config;
    if (!config) return;

    const anchors = config.anchors;
    if (anchors?.length) {
      setThresholds(
        THRESHOLDS.map((t) => {
          const match = anchors.find((anchor) => anchor[1] === t.score);
          return match ? match[0] : 0;
        }),
      );
    }

    if (config.min_weight !== undefined) setWeightA(Math.round((config.min_weight as number) * 100));
    if (config.mean_weight !== undefined) setWeightB(Math.round((config.mean_weight as number) * 100));
  }, [scoringConfig]);

  const handleThresholdChange = (index: number, value: number) => {
    setThresholds((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const weightSum = weightA + weightB;
  const weightError = weightSum !== 100 ? `a + b must equal 100%. It is currently ${weightSum}%.` : undefined;

  const handleSave = () => {
    saveScoringConfig(
      {
        anchors: THRESHOLDS.map((def, i) => [thresholds[i], def.score]),
        min_weight: weightA / 100,
        mean_weight: weightB / 100,
      },
      {
        onSuccess: () => {
          refetchReleaseStatus();
          displayToast({ title: "Saved successfully", content: "Your scoring configuration is saved." });
        },
        onError: () => {
          displayToast({ title: "Something went wrong", content: "Please try again." });
        },
      },
    );
  };

  const thresholdRows: ThresholdRowModel[] = THRESHOLDS.map((def, i) => ({
    score: def.score,
    label: def.label,
    value: thresholds[i],
    error: getValidationError(i, thresholds),
    onChange: (v: number) => handleThresholdChange(i, v),
    description: getDescription(def),
  }));

  return {
    thresholdRows,
    weightA,
    setWeightA,
    weightB,
    setWeightB,
    weightError,
    scoringConfigLoading,
    scoringConfigError,
    handleSave,
    isSaving,
  };
}

interface ThresholdConfig {
  score: number;
  default: number;
  label: string;
  range?: string;
  isFirst?: boolean;
  isLast?: boolean;
}

const THRESHOLDS: ThresholdConfig[] = [
  { score: 10, default: 50, label: "perfect", isFirst: true },
  { score: 8, default: 200, label: "excellent", range: "8.0 up to 9.9" },
  { score: 6, default: 500, label: "good", range: "6.0 up to 7.9" },
  { score: 4, default: 1000, label: "warning", range: "4.0 up to 5.9" },
  { score: 2, default: 2000, label: "concerning", range: "2.0 up to 3.9" },
  { score: 0, default: 5000, label: "critical", isLast: true },
];

const formatScoreLabel = (score: number): string => (score === 10 ? "10" : `${score}.0`);

const getDescription = (def: ThresholdConfig): string => {
  if (def.isFirst) {
    return `You consider this endpoint response time, and all shorter ones, as the threshold for a 'perfect' endpoint score (10). ← System default: ${def.default}ms.`;
  }
  if (def.isLast) {
    return `You consider this endpoint response time, and everything slower, as 'critical'. ← System default: ${def.default}ms.`;
  }
  return `You consider this endpoint response time as the threshold for a '${def.label}' endpoint score (${def.range}). ← System default: ${def.default}ms.`;
};

const getValidationError = (index: number, values: number[]): string | undefined => {
  const value = values[index];
  const prevValue = index > 0 ? values[index - 1] : undefined;
  const nextValue = index < values.length - 1 ? values[index + 1] : undefined;

  const tooSmall = prevValue !== undefined && value <= prevValue;
  const tooBig = nextValue !== undefined && value >= nextValue;

  if (!tooSmall && !tooBig) return undefined;

  if (tooSmall && tooBig) {
    const prevScore = THRESHOLDS[index - 1].score;
    const nextScore = THRESHOLDS[index + 1].score;
    return `Must be between ${prevValue}ms (${formatScoreLabel(
      prevScore,
    )} score threshold) and ${nextValue}ms (${formatScoreLabel(nextScore)} score threshold).`;
  }
  if (tooSmall) {
    const prevScore = THRESHOLDS[index - 1].score;
    return `Must be greater than ${prevValue}ms (${formatScoreLabel(prevScore)} score threshold).`;
  }
  const nextScore = THRESHOLDS[index + 1].score;
  return `Must be less than ${nextValue}ms (${formatScoreLabel(nextScore)} score threshold).`;
};
